import { type MutableRefObject, useCallback, useMemo, useRef, useState } from 'react';

import {
  getScopeBandReservesFromWidths,
  SELECTION_BAR_ITEM_COUNT_CHEVRON_RESERVE_PX,
  SELECTION_BAR_SCOPE_BAND_REFERENCE,
  type SelectionBarLayoutTierReserves,
} from '~/components/v2/components/private-selection-bar/utils/selectionBarLayoutTier';

export type SelectionBarControlMeasureRefs = {
  itemCountMin?: (node: HTMLElement | null) => void;
  scope?: (node: HTMLElement | null) => void;
  selectAll?: (node: HTMLElement | null) => void;
};

export type UseSelectionBarControlWidthsParams = {
  countLabel: string;
  hideScopeLabel: boolean;
  scopeLabel?: string;
  selectAllLabel: string;
  showItemCountChevron: boolean;
  showSelectAll: boolean;
  willOfferSelectAll: boolean;
};

const readElementWidth = (node: HTMLElement): number => {
  const rectWidth = node.getBoundingClientRect().width;
  const scrollWidth = node.scrollWidth;

  return Math.ceil(Math.max(rectWidth, scrollWidth));
};

type MeasuredElementWidthOptions = {
  cacheKey?: string;
  enabled: boolean;
  fallbackPx: number;
  shouldUpdateCache?: boolean;
  widthCache?: MutableRefObject<Map<string, number>>;
};

const useMeasuredElementWidth = ({
  cacheKey,
  enabled,
  fallbackPx,
  shouldUpdateCache = true,
  widthCache,
}: MeasuredElementWidthOptions) => {
  const observerRef = useRef<ResizeObserver | null>(null);
  const [liveWidth, setLiveWidth] = useState(fallbackPx);

  const measureRef = useCallback(
    (node: HTMLElement | null) => {
      observerRef.current?.disconnect();
      observerRef.current = null;

      if (!node || !enabled) {
        return;
      }

      const update = () => {
        const width = readElementWidth(node);

        if (width > 0) {
          const isTuckedScopeProbe = node.dataset.selectionBarScopeMeasure === 'tucked';

          if (cacheKey && widthCache && shouldUpdateCache && !isTuckedScopeProbe) {
            widthCache.current.set(cacheKey, width);
          }

          setLiveWidth((previous) => (previous === width ? previous : width));
        }
      };

      const observer = new ResizeObserver(update);
      observer.observe(node);
      observerRef.current = observer;
    },
    [cacheKey, enabled, shouldUpdateCache, widthCache],
  );

  const cachedWidth = cacheKey && widthCache ? widthCache.current.get(cacheKey) : undefined;

  const effectiveWidth = enabled || !cacheKey || !widthCache ? liveWidth : (cachedWidth ?? fallbackPx);

  return { effectiveWidth, measureRef };
};

/**
 * Live-measures select-all, item-count min, and scope widths for scope-band reserves.
 * Scope stays mounted (visually tucked via CSS). When tucked, reserves use the last visible
 * measurements so chevron moves do not shift tuck thresholds.
 */
export const useSelectionBarControlWidths = ({
  countLabel,
  hideScopeLabel,
  scopeLabel,
  selectAllLabel,
  showItemCountChevron,
  showSelectAll,
  willOfferSelectAll,
}: UseSelectionBarControlWidthsParams): {
  controlMeasureRefs: SelectionBarControlMeasureRefs;
  scopeBandReserves: Pick<
    SelectionBarLayoutTierReserves,
    'itemCountMinReservePx' | 'itemCountScopeReservePx' | 'selectAllReservePx'
  >;
} => {
  const scopeFullWidthCache = useRef(new Map<string, number>());
  const itemCountMinWidthCache = useRef(new Map<string, number>());
  const selectAllWidthCache = useRef(new Map<string, number>());

  const measureScope = !!scopeLabel;
  const measureSelectAll = willOfferSelectAll && showSelectAll;
  const measureVisibleScopeLayout = measureScope && !hideScopeLabel;
  const measureVisibleCountOnlyLayout = !hideScopeLabel || !scopeLabel;

  const itemCountMin = useMeasuredElementWidth({
    cacheKey: countLabel,
    enabled: true,
    fallbackPx: SELECTION_BAR_SCOPE_BAND_REFERENCE.itemCountMinPx,
    shouldUpdateCache: measureVisibleCountOnlyLayout,
    widthCache: itemCountMinWidthCache,
  });
  const scope = useMeasuredElementWidth({
    cacheKey: scopeLabel,
    enabled: measureScope,
    fallbackPx: SELECTION_BAR_SCOPE_BAND_REFERENCE.itemCountScopePx,
    shouldUpdateCache: measureVisibleScopeLayout,
    widthCache: scopeFullWidthCache,
  });
  const selectAll = useMeasuredElementWidth({
    cacheKey: selectAllLabel,
    enabled: measureSelectAll,
    fallbackPx: SELECTION_BAR_SCOPE_BAND_REFERENCE.selectAllButtonPx,
    widthCache: selectAllWidthCache,
  });

  const scopeBandReserves = useMemo(() => {
    const cachedCountOnlyWidth = itemCountMinWidthCache.current.get(countLabel);
    const cachedScopeWidth = scopeLabel ? scopeFullWidthCache.current.get(scopeLabel) : undefined;

    const countOnlyWidth =
      cachedCountOnlyWidth ??
      (!hideScopeLabel || !scopeLabel
        ? itemCountMin.effectiveWidth
        : SELECTION_BAR_SCOPE_BAND_REFERENCE.itemCountMinPx);

    const chevronOnCountForReserves = showItemCountChevron && (!scopeLabel || hideScopeLabel);

    let itemCountMinPx = countOnlyWidth;

    if (!scopeLabel) {
      itemCountMinPx = itemCountMin.effectiveWidth;
    } else if (chevronOnCountForReserves) {
      itemCountMinPx = countOnlyWidth + SELECTION_BAR_ITEM_COUNT_CHEVRON_RESERVE_PX;
    }

    const scopePx =
      cachedScopeWidth ??
      (!hideScopeLabel && scopeLabel ? scope.effectiveWidth : SELECTION_BAR_SCOPE_BAND_REFERENCE.itemCountScopePx);

    return getScopeBandReservesFromWidths(
      {
        itemCountMinPx,
        scopePx,
        selectAllPx: selectAll.effectiveWidth,
      },
      willOfferSelectAll,
      !!scopeLabel,
    );
  }, [
    countLabel,
    hideScopeLabel,
    itemCountMin.effectiveWidth,
    scope.effectiveWidth,
    scopeLabel,
    selectAll.effectiveWidth,
    showItemCountChevron,
    willOfferSelectAll,
  ]);

  const controlMeasureRefs = useMemo<SelectionBarControlMeasureRefs>(
    () => ({
      itemCountMin: itemCountMin.measureRef,
      scope: scope.measureRef,
      selectAll: measureSelectAll ? selectAll.measureRef : undefined,
    }),
    [itemCountMin.measureRef, measureSelectAll, scope.measureRef, selectAll.measureRef],
  );

  return { controlMeasureRefs, scopeBandReserves };
};
